/* ==========================================================================
   COLLECTION BUNDLE BUILDER  (assets/section-collection-bundle.js)
   ==========================================================================

   Three things here are load-bearing. Read these before changing anything.

   0. THE SALE CAN BE OFF. The section sends no data-tiers when the theme
      editor's "Discounts are running" box is unticked, which is the state
      between sales. DISCOUNTS is then false and the builder runs as a plain
      multi-add cart: nothing quotes a saving, and it must NOT bail out — the
      drawer, the pills and the cart sync all still have to work. (An earlier
      version returned early on an empty tier list, which would take the whole
      page down the moment a sale ended.)

   1. THE THEME DOES NOT CREATE THE DISCOUNT. Tier percentages are display
      only; the money comes from automatic Shopify discounts scoped to the
      collection. So after writing the cart we re-read /cart.js and REFUSE to
      redirect if the real discount is smaller than what the drawer quoted.
      A customer must never be charged more than the number they were shown.
      The check is asymmetric on purpose: a LARGER real discount is fine and
      goes through (they qualified for something extra), a smaller one blocks.

   2. ABSOLUTE QUANTITIES, NOT INCREMENTS. /cart/add.js increments, so a
      double-tap on Checkout silently doubles the bundle. /cart/update.js sets
      absolute quantities, which makes the write idempotent — and that is also
      why the bundle is NOT cleared on redirect to checkout: someone who
      bounces back from checkout keeps their bundle, and re-checking out
      rewrites the same quantities rather than stacking them.

   3. THE DRAWER CANNOT READ THE GRID. A customer can add books on page 1 and
      then filter or paginate away, so bundled products are routinely absent
      from the DOM. Every value the drawer renders is snapshotted into
      sessionStorage at add time. Do not "simplify" this by querying the grid
      for titles or prices — it works right up until someone uses a filter.

   4. THE BUNDLE GOES LIVE ONCE IT HAS TOUCHED THE CART. Until the first
      Checkout click the bundle is purely local — no network per pill click,
      the header bubble stays at 0. After that first write the cart contains
      bundle lines, and a bundle that disagrees with the cart is a mis-charge
      waiting to happen: a book "removed" in this drawer but left in the cart
      is a book the customer pays for. So from then on `synced` tracks what we
      wrote, every mutation pushes absolute quantities (INCLUDING an explicit
      0 for anything removed — `updates` only touches ids you name, so an
      omitted id keeps its old quantity), and edits made in Shopify's own cart
      drawer are reconciled back in. Checkout always re-sends the full map, so
      a background sync that fails can't leave a removed book in the cart.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-bundle-root]');
  if (!root) return;

  var STORAGE_VERSION = 1;
  var STORAGE_KEY = 'brave-bundle:v' + STORAGE_VERSION + ':' + (root.dataset.collectionHandle || 'default');
  var MONEY_FORMAT = root.dataset.moneyFormat || '${{amount}}';
  var VERIFY = root.dataset.verifyTotal === 'true';

  /* Tiers arrive as "2:20,4:30,6:40" — sorted ascending so the progress
     segments and the "highest qualifying tier" lookup are both well defined
     even if someone reorders them in the theme editor. */
  var TIERS = (root.dataset.tiers || '')
    .split(',')
    .map(function (pair) {
      var bits = pair.split(':');
      return { n: parseInt(bits[0], 10), pct: parseInt(bits[1], 10) };
    })
    .filter(function (t) {
      return t.n > 0 && t.pct > 0;
    })
    .sort(function (a, b) {
      return a.n - b.n;
    });

  /* No tiers = the sale is off, not a broken config. Everything below still
     runs; pct simply stays 0 and the discount UI isn't in the DOM to paint. */
  var DISCOUNTS = TIERS.length > 0;

  /* ── State ───────────────────────────────────────────────────────────── */
  var items = Object.create(null); // variantId -> {variantId, productId, title, price, url, image, qty}
  var open = false;
  var busy = false;
  var firstPaint = true;

  /* variantId -> quantity this bundle last wrote to the cart. Non-empty means
     "live": the cart holds our lines, so local edits must be pushed to it.
     Persisted with the items, because the whole point is surviving the trip
     out to /checkout and back. */
  var synced = Object.create(null);

  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var SOURCE = 'bundle-builder';
  var pushTimer = null;
  var pushPending = false;  // a debounced push is queued
  var pushInFlight = false;
  var pushAgain = false;    // state changed mid-flight; push again when it lands
  var pushRetried = false;
  var pushPromise = null;

  /* ── Elements ────────────────────────────────────────────────────────── */
  var el = {
    bar: root.querySelector('[data-bundle-bar]'),
    barTitle: root.querySelector('[data-bundle-bar-title]'),
    barPrice: root.querySelector('[data-bundle-bar-price]'),
    barWas: root.querySelector('[data-bundle-bar-was]'),
    badge: root.querySelector('[data-bundle-badge]'),
    tiers: root.querySelectorAll('[data-bundle-tier]'),
    openBtn: root.querySelector('[data-bundle-open]'),
    closeBtn: root.querySelector('[data-bundle-close]'),
    backdrop: root.querySelector('[data-bundle-backdrop]'),
    drawer: root.querySelector('[data-bundle-drawer]'),
    count: root.querySelector('[data-bundle-count]'),
    clear: root.querySelector('[data-bundle-clear]'),
    empty: root.querySelector('[data-bundle-empty]'),
    list: root.querySelector('[data-bundle-items]'),
    subtotal: root.querySelector('[data-bundle-subtotal]'),
    discountLabel: root.querySelector('[data-bundle-discount-label]'),
    savings: root.querySelector('[data-bundle-savings]'),
    total: root.querySelector('[data-bundle-total]'),
    checkout: root.querySelector('[data-bundle-checkout]'),
    checkoutLabel: root.querySelector('[data-bundle-checkout-label]'),
    error: root.querySelector('[data-bundle-error]')
  };

  var defaultBarTitle = el.barTitle ? el.barTitle.textContent.trim() : 'Build your bundle';
  var defaultBarPrice = el.barPrice ? el.barPrice.textContent.trim() : '';
  var defaultCheckoutLabel = el.checkoutLabel ? el.checkoutLabel.textContent.trim() : 'Checkout';

  /* ── Money ───────────────────────────────────────────────────────────── */
  function withCommas(str) {
    return str.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
  }

  function formatMoney(cents) {
    var value = Math.round(cents || 0);
    var sign = value < 0 ? '-' : '';
    value = Math.abs(value);

    return (
      sign +
      MONEY_FORMAT.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
        var whole = Math.floor(value / 100);
        var part = ('0' + (value % 100)).slice(-2);

        switch (name) {
          case 'amount_no_decimals':
            return withCommas(String(Math.round(value / 100)));
          case 'amount_with_comma_separator':
            return withCommas(String(whole)).replace(/,/g, '.') + ',' + part;
          case 'amount_no_decimals_with_comma_separator':
            return withCommas(String(Math.round(value / 100))).replace(/,/g, '.');
          case 'amount_with_apostrophe_separator':
            return withCommas(String(whole)).replace(/,/g, "'") + '.' + part;
          default:
            return withCommas(String(whole)) + '.' + part;
        }
      })
    );
  }

  /* ── Persistence ─────────────────────────────────────────────────────────
     Keyed per collection: bundling collection A then browsing to collection B
     must not restore A's books into B's drawer.

     Stores SELECTED items (this is opt-in, unlike complete-your-collection
     which is opt-out and stores deselections), and stores a full snapshot of
     each one rather than just an id — see note 3 at the top of this file. */
  var persistTimer = null;

  function writeStorage() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          v: STORAGE_VERSION,
          items: Object.keys(items).map(function (k) { return items[k]; }),
          synced: synced
        })
      );
    } catch (e) {
      /* private mode, quota — the bundle just won't survive a reload */
    }
  }

  function persist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(writeStorage, 200);
  }

  /* Skips the debounce. Use before navigating away: a pending 200ms timer is
     simply lost on unload, and losing `synced` means the bundle comes back
     from checkout believing it never touched the cart. */
  function persistNow() {
    clearTimeout(persistTimer);
    writeStorage();
  }

  function restore() {
    var saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      saved = null;
    }
    if (!saved || saved.v !== STORAGE_VERSION || !Array.isArray(saved.items)) return;

    /* Absent on payloads written before the cart sync existed. Missing means
       "never written to the cart", which is the safe default — the bundle
       stays local until its first checkout. */
    if (saved.synced && typeof saved.synced === 'object') {
      Object.keys(saved.synced).forEach(function (id) {
        var qty = parseInt(saved.synced[id], 10);
        if (id && qty > 0) synced[String(id)] = qty;
      });
    }

    saved.items.forEach(function (item) {
      var id = String(item && item.variantId);
      var qty = parseInt(item && item.qty, 10);
      var price = parseInt(item && item.price, 10);
      if (!id || id === 'undefined' || !(qty > 0) || !(price >= 0)) return;
      items[id] = {
        variantId: id,
        productId: String(item.productId || ''),
        title: String(item.title || ''),
        price: price,
        url: String(item.url || ''),
        image: String(item.image || ''),
        qty: qty
      };
    });
  }

  function clearPersisted() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* nothing to do */
    }
  }

  /* ── Totals ──────────────────────────────────────────────────────────── */
  function totals() {
    var count = 0;
    var subtotal = 0;

    Object.keys(items).forEach(function (id) {
      count += items[id].qty;
      subtotal += items[id].qty * items[id].price;
    });

    /* Highest tier the unit count qualifies for. Units, not distinct titles —
       two copies of one book counts as 2, matching how Shopify's "minimum
       quantity of items" rule counts. */
    var pct = 0;
    for (var i = TIERS.length - 1; i >= 0; i--) {
      if (count >= TIERS[i].n) {
        pct = TIERS[i].pct;
        break;
      }
    }

    var savings = Math.round((subtotal * pct) / 100);
    return { count: count, subtotal: subtotal, pct: pct, savings: savings, total: subtotal - savings };
  }

  function lineTotal(item, pct) {
    var gross = item.qty * item.price;
    return gross - Math.round((gross * pct) / 100);
  }

  /* ── Render ──────────────────────────────────────────────────────────── */
  function render() {
    var t = totals();
    var hasItems = t.count > 0;

    renderBar(t, hasItems);
    renderTiers(t);
    renderDrawer(t, hasItems);
    renderPills();
  }

  function renderBar(t, hasItems) {
    if (el.barTitle) {
      el.barTitle.textContent = hasItems ? 'Your bundle · ' + t.count : defaultBarTitle;
    }

    if (el.barPrice) {
      el.barPrice.textContent = hasItems ? formatMoney(t.total) : defaultBarPrice;
    }
    /* The strikethrough is suppressed at 0% on purpose: with no discount the
       "was" price equals the price, and showing the same number twice with one
       struck through reads as a bug. */
    if (el.barWas) {
      el.barWas.hidden = !(hasItems && t.pct > 0);
      el.barWas.textContent = formatMoney(t.subtotal);
    }

    if (el.badge) {
      el.badge.hidden = !hasItems || !DISCOUNTS;
      if (DISCOUNTS) {
        if (t.pct > 0) {
          el.badge.textContent = 'Save ' + t.pct + '%';
        } else {
          var need = TIERS[0].n - t.count;
          el.badge.textContent = 'Add ' + need + ' more';
        }
      }
    }
  }

  function tierLabel(tier, hit) {
    if (hit) return tier.pct + '% unlocked';
    return tier.pct + '% at ' + tier.n + (tier.n === 1 ? ' book' : ' books');
  }

  function renderTiers(t) {
    Array.prototype.forEach.call(el.tiers, function (node, i) {
      var tier = TIERS[i];
      if (!tier) return;

      var prev = i > 0 ? TIERS[i - 1].n : 0;
      var span = tier.n - prev;
      var filled = Math.min(Math.max(t.count - prev, 0) / (span > 0 ? span : 1), 1);
      var hit = t.count >= tier.n;
      var was = node.dataset.hit === 'true';

      node.dataset.hit = hit ? 'true' : 'false';
      var fill = node.querySelector('.brave-bundle__tier-fill');
      if (fill) fill.style.width = filled * 100 + '%';

      var label = node.querySelector('.brave-bundle__tier-label');
      if (label) label.textContent = tierLabel(tier, hit);

      /* Fire only on the not-hit -> hit transition, and never on the first
         paint: restoring a 6-book bundle from sessionStorage would otherwise
         set off all three bursts on page load. */
      if (hit && !was && !firstPaint) popConfetti(node);
    });
  }

  /* Confetti — same approach as snippets/free-shipping-progress.liquid: short-lived
     absolutely positioned pieces, removed once the animation is done. Each burst is
     parented to its own tier so it comes out of the bar that was just unlocked. */
  /* No white: the pieces fly up out of the navy bar and finish over the page,
     where a white piece is invisible. These all read against both. */
  var CONFETTI_COLORS = ['#0bc11f', '#d4a853', '#cd3636', '#6fc178', '#1b2a4a'];

  function popConfetti(tierNode) {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var host = tierNode.querySelector('.brave-bundle__tier-confetti');
    if (!host) {
      host = document.createElement('div');
      host.className = 'brave-bundle__tier-confetti';
      tierNode.appendChild(host);
    }

    for (var i = 0; i < 14; i++) {
      var piece = document.createElement('span');
      piece.style.left = 10 + Math.random() * 80 + '%';
      piece.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      piece.style.setProperty('--dx', (Math.random() * 56 - 28).toFixed(1) + 'px');
      piece.style.setProperty('--dy', -(22 + Math.random() * 30).toFixed(1) + 'px');
      piece.style.setProperty('--rot', Math.round(Math.random() * 540 - 270) + 'deg');
      piece.style.animationDelay = Math.round(Math.random() * 90) + 'ms';
      host.appendChild(piece);

      (function (node) {
        setTimeout(function () {
          if (node.parentNode) node.parentNode.removeChild(node);
        }, 900);
      })(piece);
    }
  }

  function renderDrawer(t, hasItems) {
    if (el.count) el.count.textContent = hasItems ? t.count + (t.count === 1 ? ' book' : ' books') : '';
    if (el.clear) el.clear.hidden = !hasItems;
    if (el.empty) el.empty.hidden = hasItems;

    if (el.list) {
      el.list.textContent = '';
      Object.keys(items).forEach(function (id) {
        el.list.appendChild(buildItem(items[id], t.pct));
      });
    }

    if (el.subtotal) el.subtotal.textContent = formatMoney(t.subtotal);
    if (el.discountLabel) {
      el.discountLabel.textContent = t.pct > 0 ? 'Bundle discount (' + t.pct + '% off)' : 'Bundle discount';
    }
    if (el.savings) el.savings.textContent = t.savings > 0 ? '−' + formatMoney(t.savings) : formatMoney(0);
    if (el.total) el.total.textContent = formatMoney(t.total);

    if (el.checkout) el.checkout.disabled = !hasItems || busy;
    if (el.checkoutLabel && !busy) {
      el.checkoutLabel.textContent = hasItems
        ? defaultCheckoutLabel + ' · ' + formatMoney(t.total)
        : defaultCheckoutLabel;
    }
  }

  function buildItem(item, pct) {
    var row = document.createElement('div');
    row.className = 'brave-bundle__item';

    var media = document.createElement(item.image ? 'img' : 'div');
    media.className = 'brave-bundle__item-media';
    if (item.image) {
      media.src = item.image;
      media.alt = '';
      media.width = 56;
      media.height = 56;
      media.loading = 'lazy';
    }
    row.appendChild(media);

    var main = document.createElement('div');
    main.className = 'brave-bundle__item-main';

    var title = document.createElement('div');
    title.className = 'brave-bundle__item-title';
    title.textContent = item.title;
    title.title = item.title;
    main.appendChild(title);

    var controls = document.createElement('div');
    controls.className = 'brave-bundle__item-controls';

    var stepper = document.createElement('div');
    stepper.className = 'brave-bundle__item-stepper';
    stepper.appendChild(stepBtn('−', 'Remove one ' + item.title, function () { setQty(item.variantId, item.qty - 1); }));

    var qty = document.createElement('span');
    qty.className = 'brave-bundle__item-qty';
    qty.textContent = item.qty;
    stepper.appendChild(qty);

    stepper.appendChild(stepBtn('+', 'Add one ' + item.title, function () { setQty(item.variantId, item.qty + 1); }));
    controls.appendChild(stepper);

    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'brave-bundle__item-remove';
    remove.textContent = 'Remove';
    remove.addEventListener('click', function () { setQty(item.variantId, 0); });
    controls.appendChild(remove);

    main.appendChild(controls);
    row.appendChild(main);

    var prices = document.createElement('div');
    prices.className = 'brave-bundle__item-prices';

    var now = document.createElement('span');
    now.className = 'brave-bundle__item-total';
    now.textContent = formatMoney(lineTotal(item, pct));
    prices.appendChild(now);

    if (pct > 0) {
      var was = document.createElement('span');
      was.className = 'brave-bundle__item-was';
      was.textContent = formatMoney(item.qty * item.price);
      prices.appendChild(was);
    }

    row.appendChild(prices);
    return row;
  }

  function stepBtn(glyph, label, onClick) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'brave-bundle__item-step';
    b.textContent = glyph;
    b.setAttribute('aria-label', label);
    b.addEventListener('click', onClick);
    return b;
  }

  /* ── Grid pills ──────────────────────────────────────────────────────────
     The server renders every card in the "Add to Bundle" state. This paints
     current state over the top, and runs again after Dawn's facet filtering
     swaps the grid out from under us (see the observer below). */
  function renderPills() {
    var slots = document.querySelectorAll('.brave-bundle-add');

    Array.prototype.forEach.call(slots, function (slot) {
      var btn = slot.querySelector('[data-bundle-add]');

      /* Hoist the product snapshot off the button the first time we see it,
         so it survives the button being replaced by the stepper. */
      if (btn && !slot.dataset.variantId) {
        slot.dataset.variantId = btn.dataset.variantId;
        slot.dataset.productId = btn.dataset.productId;
        slot.dataset.title = btn.dataset.title;
        slot.dataset.price = btn.dataset.price;
        slot.dataset.url = btn.dataset.url;
        slot.dataset.image = btn.dataset.image || '';
      }

      var id = slot.dataset.variantId;
      if (!id) return; // sold out, or a multi-variant "Choose options" link

      var item = items[id];
      if (item) {
        paintStepper(slot, item);
      } else {
        paintAdd(slot);
      }
    });
  }

  function paintAdd(slot) {
    if (slot.dataset.state === 'add') return;

    /* The server already renders this exact button. Leaving it in place (rather
       than replacing it with an identical one) avoids pointless DOM churn on
       every render and keeps the markup the theme shipped. */
    var existing = slot.querySelector('[data-bundle-add]');
    if (existing) {
      slot.dataset.state = 'add';
      return;
    }

    slot.dataset.state = 'add';
    slot.textContent = '';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'brave-bundle-add__btn';
    btn.setAttribute('data-bundle-add', '');
    btn.setAttribute('data-bundle-action', 'add');
    btn.textContent = 'Add to Bundle';
    slot.appendChild(btn);
  }

  function paintStepper(slot, item) {
    if (slot.dataset.state === 'stepper') {
      var qtyNode = slot.querySelector('.brave-bundle-add__qty');
      if (qtyNode) qtyNode.textContent = item.qty;
      return;
    }
    slot.dataset.state = 'stepper';
    slot.textContent = '';

    var wrap = document.createElement('div');
    wrap.className = 'brave-bundle-add__stepper';

    wrap.appendChild(cardStep('−', 'dec', 'Remove one ' + item.title));

    var qty = document.createElement('span');
    qty.className = 'brave-bundle-add__qty';
    qty.textContent = item.qty;
    wrap.appendChild(qty);

    wrap.appendChild(cardStep('+', 'inc', 'Add one ' + item.title));

    slot.appendChild(wrap);
  }

  function cardStep(glyph, action, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'brave-bundle-add__step';
    b.setAttribute('data-bundle-action', action);
    b.textContent = glyph;
    b.setAttribute('aria-label', label);
    return b;
  }

  /* One delegated handler for the whole grid. Card controls get re-created on
     every render and wholesale replaced by Dawn's facet filtering, so binding
     per button means re-binding forever and leaking listeners. */
  document.addEventListener('click', function (e) {
    var control = e.target.closest ? e.target.closest('[data-bundle-action], [data-bundle-add]') : null;
    if (!control) return;

    var slot = control.closest('.brave-bundle-add');
    if (!slot || !slot.dataset.variantId) return;

    var id = slot.dataset.variantId;
    var action = control.dataset.bundleAction || 'add';
    var current = items[id] ? items[id].qty : 0;

    if (action === 'dec') setQty(id, current - 1);
    else setQty(id, current + 1, snapshotFrom(slot));
  });

  function snapshotFrom(slot) {
    return {
      variantId: slot.dataset.variantId,
      productId: slot.dataset.productId || '',
      title: slot.dataset.title || '',
      price: parseInt(slot.dataset.price, 10) || 0,
      url: slot.dataset.url || '',
      image: slot.dataset.image || ''
    };
  }

  /* ── Mutations ───────────────────────────────────────────────────────── */
  function setQty(variantId, qty, snapshot) {
    var id = String(variantId);
    if (!id) return;

    if (qty <= 0) {
      delete items[id];
    } else if (items[id]) {
      items[id].qty = qty;
    } else if (snapshot) {
      items[id] = Object.assign({}, snapshot, { variantId: id, qty: qty });
    } else {
      return;
    }

    hideError();
    persist();
    schedulePush();
    render();
  }

  function clearAll() {
    items = Object.create(null);
    hideError();

    /* When live, `synced` has to survive the clear — it is the only record of
       which cart lines need zeroing. Wiping storage here would strand every
       bundled book in the customer's cart. */
    if (isLive()) {
      persist();
      schedulePush();
    } else {
      clearPersisted();
    }

    render();
  }

  /* ── Cart sync ───────────────────────────────────────────────────────────
     Everything below only runs once the bundle has been written to the cart
     at least once (`isLive()`). Before that the cart is untouched and there is
     nothing to keep in step. */

  function isLive() {
    for (var _ in synced) return true;
    return false;
  }

  function syncPending() {
    return pushPending || pushInFlight;
  }

  /* Absolute quantities for everything in the bundle, plus an explicit 0 for
     every id we previously wrote that is no longer in it. The zeros are the
     whole point: /cart/update.js leaves unnamed ids alone, so a removed book
     stays in the cart forever unless we name it. */
  function buildUpdates() {
    var updates = {};
    Object.keys(synced).forEach(function (id) {
      updates[id] = 0;
    });
    Object.keys(items).forEach(function (id) {
      updates[id] = items[id].qty;
    });
    return updates;
  }

  function adoptSynced(updates) {
    synced = Object.create(null);
    Object.keys(updates).forEach(function (id) {
      if (updates[id] > 0) synced[id] = updates[id];
    });
  }

  function schedulePush(delay) {
    if (!isLive()) return;
    pushPending = true;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(pushCart, typeof delay === 'number' ? delay : 300);
  }

  function pushCart() {
    pushPending = false;
    if (!isLive()) return;
    if (pushInFlight) {
      pushAgain = true;
      return;
    }

    pushInFlight = true;
    var updates = buildUpdates();

    pushPromise = writeCart(updates)
      .then(function (cart) {
        /* Adopting the map we SENT, not the cart we got back, keeps this
           correct when the customer clicked again mid-flight: anything they
           changed since is covered by the pushAgain pass below. */
        adoptSynced(updates);
        persist();
        applySections(cart && cart.sections);
        announce(cart);
        pushRetried = false;
      })
      .catch(function () {
        /* Deliberately silent. The cart is stale, not wrong, and checkout
           re-sends this same map before redirecting — so a failed sync can
           never put a removed book on the order. One retry, then let it be. */
        if (!pushRetried) {
          pushRetried = true;
          pushAgain = true;
        }
      })
      .then(function () {
        pushInFlight = false;
        if (pushAgain) {
          pushAgain = false;
          schedulePush(pushRetried ? 1500 : 0);
        }
      });

    return pushPromise;
  }

  function writeCart(updates) {
    return postJSON('/cart/update.js', {
      updates: updates,
      sections: SECTIONS,
      sections_url: window.location.pathname
    });
  }

  /* ── Repainting Shopify's own cart UI ────────────────────────────────────
     Dawn's CartDrawer.renderContents() does this, but it also calls open() —
     it exists to pop the drawer after an add. We need the same swap silently,
     so this repeats the parse rather than reusing it. */
  function sectionNode(html, selector) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector) || null;
  }

  function applySections(sections) {
    if (!sections) return;
    applyDrawer(sections['cart-drawer']);
    applyBubble(sections['cart-icon-bubble']);
  }

  /* Tell the rest of the theme the cart moved — the free-shipping bar and the
     cart upsell listen on this channel and would otherwise sit at the old
     total. `source` is how our own subscriber below ignores the echo. */
  function announce(cart) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS !== 'object') return;
    try {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
    } catch (e) {
      /* a third-party subscriber threw; not ours to handle */
    }
  }

  function applyDrawer(html) {
    var target = document.getElementById('CartDrawer');
    var incoming = sectionNode(html, '#CartDrawer');
    if (!target || !incoming) return;

    target.innerHTML = incoming.innerHTML;

    /* The is-empty class lives on the <cart-drawer> host, OUTSIDE the node we
       just replaced, so an emptied cart keeps rendering as full without this. */
    var host = document.querySelector('cart-drawer');
    var incomingHost = sectionNode(html, 'cart-drawer');
    if (host && incomingHost) {
      if (incomingHost.classList.contains('is-empty')) host.classList.add('is-empty');
      else host.classList.remove('is-empty');
    }
  }

  function applyBubble(html) {
    var target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    /* innerHTML, never the node itself: cart-drawer.js bound the open-drawer
       listener to the <a> in its constructor and never rebinds. */
    var incoming = sectionNode(html, '.shopify-section') || sectionNode(html, 'body');
    if (incoming) target.innerHTML = incoming.innerHTML;
  }

  /* Re-fetch both sections from the server. Used when we arrive back on a page
     whose HTML predates the cart write — the bfcache case below. */
  function refreshCartUI() {
    if (!isLive()) return Promise.resolve();

    return fetch(window.location.pathname + '?sections=' + SECTIONS, {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(applySections)
      .catch(function () {});
  }

  /* ── Reconcile: the cart wins ────────────────────────────────────────────
     The customer can edit the same lines in Shopify's cart drawer. For ids we
     wrote, the cart is the truth — otherwise a book deleted there gets quietly
     re-added by the next bundle checkout. */
  function reconcile() {
    if (!isLive() || syncPending()) return Promise.resolve();

    return getCart()
      .then(function (cart) {
        var real = Object.create(null);
        (cart.items || []).forEach(function (line) {
          var id = String(line.variant_id);
          real[id] = (real[id] || 0) + line.quantity;
        });

        var changed = false;
        var needsPush = false;

        Object.keys(synced).forEach(function (id) {
          var qty = real[id] || 0;

          if (!items[id]) {
            /* Removed here, but the write never landed — they navigated away
               inside the debounce. Keep it in `synced` so the next push still
               zeroes it; dropping it is exactly how a removed book reaches
               checkout. */
            if (qty > 0) needsPush = true;
            else {
              delete synced[id];
              changed = true;
            }
            return;
          }

          if (qty <= 0) {
            delete items[id];
            delete synced[id];
            changed = true;
            return;
          }

          if (items[id].qty !== qty) {
            items[id].qty = qty;
            changed = true;
          }
          if (synced[id] !== qty) {
            synced[id] = qty;
            changed = true;
          }
        });

        if (changed) {
          persist();
          render();
        }
        if (needsPush) schedulePush(0);
      })
      .catch(function () {});
  }

  /* ── Drawer open/close ───────────────────────────────────────────────── */
  var lastFocus = null;

  function setOpen(next) {
    open = next;
    if (el.drawer) el.drawer.dataset.open = open ? 'true' : 'false';
    if (el.backdrop) el.backdrop.dataset.open = open ? 'true' : 'false';
    if (el.bar) el.bar.dataset.hidden = open ? 'true' : 'false';

    if (open) {
      lastFocus = document.activeElement;
      if (el.closeBtn) el.closeBtn.focus();
    } else if (lastFocus && lastFocus.focus) {
      lastFocus.focus();
    }
  }

  /* ── Errors ──────────────────────────────────────────────────────────── */
  function showError(html) {
    if (!el.error) return;
    el.error.innerHTML = html;
    el.error.hidden = false;
  }

  function hideError() {
    if (el.error) el.error.hidden = true;
  }

  function setBusy(state, label) {
    busy = state;
    if (el.checkout) el.checkout.disabled = state || totals().count === 0;
    if (el.checkoutLabel && label) el.checkoutLabel.textContent = label;
    if (!state) render();
  }

  /* ── Checkout ────────────────────────────────────────────────────────── */
  function postJSON(path, payload) {
    return fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      return res.json().then(function (body) {
        if (!res.ok) throw new Error((body && body.description) || path + ' ' + res.status);
        return body;
      });
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  function checkout() {
    var t = totals();
    if (t.count === 0 || busy) return;

    hideError();
    setBusy(true, 'Working…');

    /* Stand down any queued background sync — this write supersedes it, and
       letting an older one land after us would undo it. */
    clearTimeout(pushTimer);
    pushPending = false;
    pushAgain = false;

    var updates = buildUpdates();
    var settled = pushInFlight && pushPromise ? pushPromise.catch(function () {}) : Promise.resolve();

    settled
      .then(function () {
        return writeCart(updates);
      })
      .then(function (cart) {
        adoptSynced(updates);
        persistNow();
        /* Repaint the header bubble and cart drawer BEFORE leaving. If they
           bounce back from checkout the page may come out of the bfcache with
           this markup intact, in which case it is already correct. */
        applySections(cart && cart.sections);
        announce(cart);

        if (!VERIFY) return null;
        return getCart();
      })
      .then(function (cart) {
        if (cart) {
          var problem = verify(cart, t);
          if (problem) {
            setBusy(false);
            showError(problem);
            return;
          }
        }
        /* Deliberately NOT clearing the bundle here — see note 2 up top. */
        window.location.href = '/checkout';
      })
      .catch(function (err) {
        setBusy(false);
        showError(
          '<strong>We couldn’t start your checkout.</strong> Nothing has been charged. ' +
            'Please try again, or contact us if it keeps happening.' +
            '<br><small>' + String(err && err.message ? err.message : err) + '</small>'
        );
      });
  }

  /* Returns an error string when the real cart disagrees with the quote, or
     null when it's safe to proceed. */
  function verify(cart, quoted) {
    var lines = cart.items || [];
    var byVariant = Object.create(null);
    lines.forEach(function (line) {
      byVariant[String(line.variant_id)] = line;
    });

    var ids = Object.keys(items);
    var missing = [];
    var actualTotal = 0;

    for (var i = 0; i < ids.length; i++) {
      var want = items[ids[i]];
      var line = byVariant[ids[i]];
      if (!line || line.quantity !== want.qty) {
        missing.push(want.title || ids[i]);
        continue;
      }
      /* final_line_price is after line-level discount allocations, which is
         exactly where an automatic product discount lands. */
      actualTotal += typeof line.final_line_price === 'number' ? line.final_line_price : line.original_line_price;
    }

    if (missing.length) {
      return (
        '<strong>Some books couldn’t be added.</strong> ' +
        'They may have sold out while you were building your bundle: ' +
        missing.join(', ') +
        '. Remove them and try again.'
      );
    }

    /* Percentage discounts are allocated per line, so each line can round
       independently — allow a cent per line before calling it a mismatch. */
    var tolerance = Math.max(ids.length, 2);

    if (actualTotal > quoted.total + tolerance) {
      /* With the sale off there is no discount to blame, so the headline has
         to be about the price moving, not about a discount failing. */
      return (
        (DISCOUNTS
          ? '<strong>This bundle’s discount didn’t apply.</strong> '
          : '<strong>These prices have changed.</strong> ') +
        'We quoted ' + formatMoney(quoted.total) + ' but checkout would charge ' + formatMoney(actualTotal) +
        ', so we’ve stopped here rather than overcharge you. Nothing has been charged — ' +
        'please contact us and we’ll sort it out.'
      );
    }

    return null;
  }

  /* ── Wiring ──────────────────────────────────────────────────────────── */
  if (el.openBtn) el.openBtn.addEventListener('click', function () { setOpen(true); reconcile(); });
  if (el.closeBtn) el.closeBtn.addEventListener('click', function () { setOpen(false); });
  if (el.backdrop) el.backdrop.addEventListener('click', function () { setOpen(false); });
  if (el.clear) el.clear.addEventListener('click', clearAll);
  if (el.checkout) el.checkout.addEventListener('click', checkout);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && open) setOpen(false);
  });

  /* Dawn's facets.js replaces the grid's innerHTML on filter/sort/paginate,
     which wipes every pill back to its server-rendered "Add" state. Observing
     the container repaints them instead of requiring an edit to facets.js. */
  var gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer && 'MutationObserver' in window) {
    var repaint = null;
    new MutationObserver(function () {
      clearTimeout(repaint);
      repaint = setTimeout(renderPills, 50);
    }).observe(gridContainer, { childList: true, subtree: true });
  }

  /* ── Staying in step with the cart ───────────────────────────────────────
     All of these no-op until the bundle has been written to the cart once. */

  /* Coming back from /checkout. The page is served from the bfcache (or disk),
     so its header bubble and cart drawer are the markup from BEFORE the cart
     write and show an empty cart. Both have to be re-fetched. */
  window.addEventListener('pageshow', function (e) {
    if (!isLive()) return;
    if (!e.persisted && !backForward()) return;
    refreshCartUI();
    reconcile();
  });

  function backForward() {
    try {
      var nav = performance.getEntriesByType('navigation')[0];
      return !!nav && nav.type === 'back_forward';
    } catch (err) {
      return false;
    }
  }

  /* Checkout opened in another tab, or the customer simply came back to this
     one. Throttled — visibilitychange fires on every tab switch. */
  var lastVisibleSync = 0;
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible' || !isLive()) return;
    var now = Date.now();
    if (now - lastVisibleSync < 5000) return;
    lastVisibleSync = now;
    refreshCartUI();
    reconcile();
  });

  /* Edits made in Shopify's own cart drawer. cart-drawer.js fires the DOM
     event; cart.js publishes on the pub/sub channel. Listen to both — which
     one fires depends on where the change came from — and let reconcile()
     dedupe, since it is a no-op when nothing actually differs. */
  document.addEventListener('cart:updated', function () {
    reconcile();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      if (data && data.source === SOURCE) return; // our own write
      reconcile();
    });
  }

  restore();
  render();
  firstPaint = false;

  /* A restored bundle may already own cart lines that were edited elsewhere
     since. Settle that before the customer touches anything. */
  reconcile();
})();
